import { useState } from "react";
import { toast } from "sonner";
import { AlertOctagon, AlertTriangle, Info, Check } from "lucide-react";
import { useApi, useFetch, errMsg } from "../lib/api";
import { PageHeader, Empty } from "../components/shared";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

const SEV = {
  CRITICAL: [AlertOctagon, "text-rose-400", "border-l-rose-500"],
  WARNING: [AlertTriangle, "text-amber-400", "border-l-amber-500"],
  INFO: [Info, "text-cyan-400", "border-l-cyan-500"],
};

export default function Alerts() {
  const api = useApi();
  const [status, setStatus] = useState("open");
  const { data, reload } = useFetch(`/alerts?status=${status}`);

  const resolve = async (a) => {
    try { await api.patch(`/alerts/${a.id}/resolve`); toast.success("Alert resolved"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Watchtower" title="Compliance alerts" desc="Raised automatically for payment holds, minimum wage breaches, CLRA headcount overruns and manual overrides." />
      <Tabs value={status} onValueChange={setStatus} className="mb-6">
        <TabsList className="bg-[#121824] border border-[#1F2A3E]">
          <TabsTrigger value="open" data-testid="alerts-tab-open">Open</TabsTrigger>
          <TabsTrigger value="resolved" data-testid="alerts-tab-resolved">Resolved</TabsTrigger>
          <TabsTrigger value="all" data-testid="alerts-tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="grid gap-3">
        {(data || []).map((a, i) => {
          const [Icon, tone, border] = SEV[a.severity] || SEV.INFO;
          return (
            <div key={a.id} data-testid="alert-item-card" style={{ animationDelay: `${i * 40}ms` }}
              className={`c-surface c-rise border-l-4 ${border} p-5 flex gap-4 items-start`}>
              <Icon size={20} className={`${tone} shrink-0 mt-0.5`} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`font-mono text-[10px] tracking-widest ${tone}`}>{a.severity}</span>
                  {a.code && <span className="font-mono text-[10px] text-slate-500">{a.code}</span>}
                  {a.month && <span className="font-mono text-[10px] text-slate-500">· {a.month}</span>}
                </div>
                <div className="font-semibold mt-1">{a.title}</div>
                <div className="text-sm text-slate-400 mt-1">{a.description}</div>
                <div className="text-xs text-slate-600 mt-2 font-mono">{a.vendor_name && `${a.vendor_name} · `}{new Date(a.created_at).toLocaleString("en-IN")}{a.email_status && ` · ${a.email_status}`}</div>
              </div>
              {!a.resolved && (
                <button onClick={() => resolve(a)} data-testid="alert-resolve-btn"
                  className="shrink-0 flex items-center gap-1.5 text-xs rounded-md border border-[#1F2A3E] px-3 py-1.5 text-slate-300 hover:bg-[#1A2234] hover:text-white transition-colors">
                  <Check size={13} />Resolve</button>
              )}
            </div>
          );
        })}
        {data?.length === 0 && <div className="c-surface"><Empty text="No alerts here." /></div>}
      </div>
    </div>
  );
}
